'use client';

import { useState } from 'react';

import { breakpoints } from '@/directory/styling';

import Dialog from '@/components/atoms/Dialog/Dialog';

import { useKeyboardEventHandler } from '@/hooks';
import EmptyGalleryPlaceholder from './EmptyGalleryPlaceholder';
import {
  FullImage,
  FullImageContainer,
  InvisibleLink,
  LightboxImage,
  NextButton,
  NextPrevIcon,
  PreviousButton,
  renderIf,
  Thumbnail,
  ThumbnailContainer,
  ThumbnailRow,
} from './parts';

interface asPictureSourcesProps {
  small: string;
  medium: string;
  large: string;
}
const asPictureSources = ({ small, medium, large }: asPictureSourcesProps) => [
  { src: large, minWidthPx: breakpoints.desktop },
  { src: medium, minWidthPx: breakpoints.phone },
  { src: small, minWidthPx: 0 },
];

const skipLinkSelector = 'endofgallery';

interface ImageGalleryProps {
  images: any[];
}
export const ImageGallery = ({ images = [] }: ImageGalleryProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const openLightbox = () => setShowLightbox(true);

  const imageCount = images.length;

  const RenderIfAtLeastOneImage = renderIf(imageCount >= 1);
  const RenderIfAtLeastTwoImages = renderIf(imageCount >= 2);

  const goToPreviousImage = () =>
    setCurrentIndex(
      currentIndex - 1 < 0 ? images.length - 1 : currentIndex - 1,
    );
  const goToNextImage = () =>
    setCurrentIndex(
      currentIndex + 1 > images.length - 1 ? 0 : currentIndex + 1,
    );

  const { onKeyboardEvent } = useKeyboardEventHandler({ Enter: openLightbox });

  if (!imageCount) {
    return <EmptyGalleryPlaceholder />;
  }

  const currentImage = images[currentIndex];
  const currentImageSources = asPictureSources(currentImage);

  return (
    <>
      <RenderIfAtLeastOneImage>
        <InvisibleLink href={`#${skipLinkSelector}`} tabIndex='0'>
          skip gallery
        </InvisibleLink>
        <FullImageContainer>
          <RenderIfAtLeastTwoImages>
            <PreviousButton
              aria-label='View previous photo'
              name='prevous-image-selector'
              onClick={goToPreviousImage}
            >
              <NextPrevIcon icon={'chevron-left'} />
            </PreviousButton>
            <NextButton
              aria-label='View next photo'
              name='next-image-selector'
              onClick={goToNextImage}
            >
              <NextPrevIcon icon={'chevron-right'} />
            </NextButton>
          </RenderIfAtLeastTwoImages>
          <FullImage
            tabIndex={0}
            onClick={openLightbox}
            onKeyDown={onKeyboardEvent}
            sources={currentImageSources}
            fallback={currentImage.medium}
            blurUpSrc={currentImage.tiny}
            alt=''
          />
        </FullImageContainer>
        <Dialog isOpen={showLightbox} setIsOpen={setShowLightbox}>
          <LightboxImage
            sources={currentImageSources}
            fallback={currentImage.large}
            blurUpSrc={currentImage.tiny}
            alt=''
          />
        </Dialog>
      </RenderIfAtLeastOneImage>
      <RenderIfAtLeastTwoImages>
        <ThumbnailRow>
          {images.map(({ tiny, small: source }, i) => (
            <ThumbnailContainer
              aria-label={`View photo ${i + 1} of ${images.length}`}
              name='image-selector'
              type='button'
              key={tiny}
              onClick={() => setCurrentIndex(i)}
              $isLast={i === imageCount - 1}
            >
              <Thumbnail src={source} position='initial' alt='' />
            </ThumbnailContainer>
          ))}
        </ThumbnailRow>
      </RenderIfAtLeastTwoImages>
      <div id={skipLinkSelector} />
    </>
  );
};

export default ImageGallery;
